import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Typography,
  useTheme,
  useMediaQuery,
  LinearProgress,
  Chip,
} from '@mui/material';
import { Close as CloseIcon, Download as DownloadIcon, Timer as TimerIcon } from '@mui/icons-material';

interface PdfDialogProps {
  open: boolean;
  onClose: () => void;
  pdfUrl: string;
  title?: string;
  allowDownload?: boolean;
  enforceViewing?: boolean;
  minimumViewingTime?: number; // in seconds
  onViewingCompleted?: () => void;
}

const PdfDialog: React.FC<PdfDialogProps> = ({
  open,
  onClose,
  pdfUrl,
  title = 'מסמך PDF',
  allowDownload = true,
  enforceViewing = false,
  minimumViewingTime = 30,
  onViewingCompleted,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [viewingTime, setViewingTime] = React.useState(0);
  const [isViewingCompleted, setIsViewingCompleted] = React.useState(false);
  const [mobileViewingCompleted, setMobileViewingCompleted] = React.useState(false);
  const timerRef = React.useRef<NodeJS.Timeout | null>(null);

  // Timer effect (only for desktop)
  React.useEffect(() => {
    if (open && enforceViewing && !isViewingCompleted && !isMobile) {
      timerRef.current = setInterval(() => {
        setViewingTime(prev => {
          const newTime = prev + 1;
          if (newTime >= minimumViewingTime) {
            setIsViewingCompleted(true);
            onViewingCompleted?.();
            if (timerRef.current) {
              clearInterval(timerRef.current);
            }
          }
          return newTime;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [open, enforceViewing, isViewingCompleted, minimumViewingTime, onViewingCompleted, isMobile]);

  // Reset timer when dialog closes
  React.useEffect(() => {
    if (!open) {
      setViewingTime(0);
      setIsViewingCompleted(false);
      setMobileViewingCompleted(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  }, [open]);

  // Handle mobile PDF viewing completion
  const handleMobilePdfView = () => {
    setMobileViewingCompleted(true);
    onViewingCompleted?.();
    window.open(pdfUrl, '_blank', 'noopener,noreferrer');
  };

  const handleDownload = () => {
    // Mark as viewed when user downloads on mobile
    if (isMobile) {
      setMobileViewingCompleted(true);
      onViewingCompleted?.();
    }
    
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = title.replace(/\s+/g, '_') + '.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const remainingTime = Math.max(0, minimumViewingTime - viewingTime);
  const progress = Math.min(100, (viewingTime / minimumViewingTime) * 100);
  
  // Check if viewing is completed (desktop timer or mobile action)
  const viewingCompleted = isViewingCompleted || (isMobile && mobileViewingCompleted);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      sx={{ 
        direction: 'rtl',
        '& .MuiDialog-paper': {
          height: isMobile ? '100%' : '90vh',
          maxHeight: isMobile ? '100%' : '90vh',
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          flexDirection: 'column',
          borderBottom: '1px solid',
          borderColor: 'divider',
          pb: 1,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: enforceViewing ? 1 : 0 }}>
          <Typography variant="h6" component="div">
            {title}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {allowDownload && (
              <IconButton
                onClick={handleDownload}
                size="small"
                title="הורד PDF"
                sx={{ color: 'primary.main' }}
              >
                <DownloadIcon />
              </IconButton>
            )}
            <IconButton
              onClick={onClose}
              size="small"
              title="סגור"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        
        {/* Timer UI */}
        {enforceViewing && (
          <Box sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TimerIcon fontSize="small" color={viewingCompleted ? 'success' : 'primary'} />
                <Typography variant="body2" color={viewingCompleted ? 'success.main' : 'text.secondary'}>
                  {viewingCompleted ? 'הקריאה הושלמה!' : (isMobile ? 'יש לצפות במסמך' : `נותרו ${remainingTime} שניות`)}
                </Typography>
              </Box>
              <Chip
                label={viewingCompleted ? 'הושלם' : (isMobile ? 'ממתין' : `${Math.floor(progress)}%`)}
                color={viewingCompleted ? 'success' : 'primary'}
                size="small"
              />
            </Box>
            <LinearProgress
              variant="determinate"
              value={isMobile ? (mobileViewingCompleted ? 100 : 0) : progress}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: 'grey.200',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 3,
                  backgroundColor: viewingCompleted ? 'success.main' : 'primary.main',
                },
              }}
            />
          </Box>
        )}
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '500px',
          }}
        >
          {isMobile ? (
            /* Mobile: Show direct link and download options instead of iframe */
            <Box sx={{ textAlign: 'center', p: 3, width: '100%' }}>
              <Typography variant="h6" sx={{ mb: 3, color: 'primary.main' }}>
                צפייה במסמך
              </Typography>
              
              <Typography variant="body1" sx={{ mb: 3 }}>
                לצפייה במסמך, בחר באחת מהאפשרויות הבאות:
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 300, mx: 'auto' }}>
                {/* Open in new tab - works better on mobile */}
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleMobilePdfView}
                  sx={{ py: 1.5 }}
                >
                  פתח במסך מלא
                </Button>
                
                {/* Download option */}
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleDownload}
                  startIcon={<DownloadIcon />}
                  sx={{ py: 1.5 }}
                >
                  הורד לצפייה
                </Button>
              </Box>
              
              <Typography variant="caption" sx={{ mt: 3, display: 'block', color: 'text.secondary' }}>
                המסמך ייפתח באפליקציית הצפייה של המכשיר שלך
              </Typography>
            </Box>
          ) : (
            /* Desktop: Use iframe as before */
            <Box
              component="iframe"
              src={`${pdfUrl}#toolbar=1&navpanes=1&scrollbar=1`}
              sx={{
                width: '100%',
                height: '100%',
                minHeight: '70vh',
                border: 'none',
                borderRadius: 1,
              }}
              title={title}
            />
          )}
        </Box>

        {/* Fallback for desktop browsers that don't support PDF embedding */}
        {!isMobile && (
          <Box
            sx={{
              display: 'none', // Will be shown via CSS if iframe fails
              textAlign: 'center',
              p: 4,
              '& iframe:not([src])': {
                display: 'none',
              },
              '& iframe:not([src]) + &': {
                display: 'block',
              },
            }}
          >
            <Typography variant="body1" sx={{ mb: 2 }}>
              הדפדפן שלך אינו תומך בהצגת PDF. אנא הורד את הקובץ לצפייה.
            </Typography>
            <Button
              variant="contained"
              onClick={handleDownload}
              startIcon={<DownloadIcon />}
            >
              הורד PDF
            </Button>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid', borderColor: 'divider', p: 2 }}>
        <Button onClick={onClose} variant="outlined">
          סגור
        </Button>
        {allowDownload && (
          <Button
            onClick={handleDownload}
            variant="contained"
            startIcon={<DownloadIcon />}
          >
            הורד
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PdfDialog;
